import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Alert } from '../../components/ui/Alert';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

export const ResetPassword = () => {
  const navigate = useNavigate();
  const { session, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) throw error;

      await supabase.auth.signOut();
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    if (done) {
      return (
        <div className="text-center">
          <Alert variant="success" message="Your password has been updated successfully." className="mb-6" />
          <Button className="w-full" onClick={() => navigate('/login', { replace: true })}>
            Continue to login
          </Button>
        </div>
      );
    }

    if (authLoading) {
      return <p className="text-center text-sm text-slate-600">Verifying your reset link…</p>;
    }

    if (!session) {
      return (
        <div className="text-center">
          <Alert
            variant="error"
            message="This password reset link is invalid or has expired. Please request a new one."
            className="mb-6"
          />
          <Link to="/forgot-password">
            <Button className="w-full">Request a new link</Button>
          </Link>
        </div>
      );
    }

    return (
      <>
        {error && <Alert variant="error" message={error} className="mb-6" />}

        <form className="space-y-6" onSubmit={handleSubmit}>
          <Input
            label="New Password"
            type={showPassword ? 'text' : 'password'}
            required
            minLength={6}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
          />

          <Input
            label="Confirm New Password"
            type={showPassword ? 'text' : 'password'}
            required
            minLength={6}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="••••••••"
          />

          <button
            type="button"
            onClick={() => setShowPassword((s) => !s)}
            className="flex items-center gap-2 text-sm text-slate-600 hover:text-meetoi-navy"
          >
            <SafeIcon icon={showPassword ? FiIcons.FiEyeOff : FiIcons.FiEye} />
            {showPassword ? 'Hide passwords' : 'Show passwords'}
          </button>

          <Button type="submit" className="w-full" isLoading={loading}>
            Update Password
          </Button>
        </form>
      </>
    );
  };

  return (
    <div className="min-h-[80vh] flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="w-12 h-12 bg-meetoi-navy rounded-xl flex items-center justify-center mx-auto mb-6">
          <SafeIcon icon={FiIcons.FiLayers} className="text-white text-2xl" />
        </div>
        <h2 className="text-center text-3xl font-extrabold text-meetoi-navy">Reset Your Password</h2>
        <p className="mt-2 text-center text-sm text-slate-600">
          Please enter your new password below.
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-xl sm:px-10 border border-slate-100">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};